import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../../lib/utils';
import {
  borderPrimary,
  mediumText,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  tertiaryText,
  textDarkGreen,
} from '../../../../../styling/stylingHelper';
import { FrontendStaffUser } from '../../../../../services/staff/staffService';
import { AccessType, getAccessTypeFromRestrictions } from '../../staffAccessUtils';
import { SectionHeading, sectionPanelClass, sectionRowClass } from './sectionStyles';

interface Restaurant {
  id: string;
  name: string;
}

interface StaffRestaurantAccessSectionProps {
  user: FrontendStaffUser;
  restaurants: Restaurant[];
  canManage: boolean;
  onRemoveRestaurantAccess: (restaurantId: string, restaurantName: string) => void;
  onManageAccess: (restaurantId: string) => void;
  onAddRestaurant: () => void;
}

const StaffRestaurantAccessSection: React.FC<StaffRestaurantAccessSectionProps> = ({
  user,
  restaurants,
  canManage,
  onRemoveRestaurantAccess,
  onManageAccess,
  onAddRestaurant,
}) => {
  const { t } = useTranslation();

  const getRestaurantName = (id: string) => restaurants.find((r) => r.id === id)?.name || id;
  const hasAllRestaurants = user.restaurant_ids.length === restaurants.length;
  // Restaurants outside the viewer's own access can't be named, so they collapse
  // into a count instead of being listed.
  const inScopeRestaurantIds = user.restaurant_ids.filter((id) => restaurants.some((r) => r.id === id));
  const outOfScopeCount = user.restaurant_ids.length - inScopeRestaurantIds.length;

  const accessTypeLabel = (type: AccessType) => {
    if (type === 'food') return t('staff.foodOnly');
    if (type === 'drinks') return t('staff.drinksOnly');
    if (type === 'mixed') return `${t('staff.foodOnly')} / ${t('staff.drinksOnly')}`;
    return t('staff.allAccess');
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <SectionHeading>{t('staff.restaurantAccess')}</SectionHeading>
        {canManage && !hasAllRestaurants && (
          <button
            type="button"
            onClick={onAddRestaurant}
            className={cn(smallTextSize, semiBoldText, textDarkGreen, 'inline-flex items-center gap-1 hover:opacity-80')}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
              <path
                d="M8.0013 2.66699L8.0013 13.3337M13.3346 8.00033L2.66797 8.00033"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="underline underline-offset-2">{t('staff.addRestaurant')}</span>
          </button>
        )}
      </div>

      <div className={cn(sectionPanelClass, 'space-y-2.5 sm:space-y-4 p-2.5 sm:p-3')}>
        {inScopeRestaurantIds.length > 0
          ? inScopeRestaurantIds.map((rid) => {
              const name = getRestaurantName(rid);
              const canRemove = user.restaurant_ids.length > 1;
              const restaurantAccessType = getAccessTypeFromRestrictions(user.restaurant_access?.[rid]);

              return (
                <div key={rid} className={cn(sectionRowClass, 'flex items-center justify-between gap-2')}>
                  <div className="min-w-0">
                    <p className={cn(primaryText, semiBoldText, 'text-[14px] sm:text-[16px] leading-5 sm:leading-6 truncate')}>
                      {name}
                    </p>
                    {/* The spelled-out label carries the same information as the card's
                        Food/Drinks pills, so the pills would only repeat it here. */}
                    <p className={cn(tertiaryText, 'text-[14px] leading-5 sm:leading-6 font-normal mt-1 sm:mt-2')}>
                      {accessTypeLabel(restaurantAccessType)}
                    </p>
                  </div>

                  {canManage && (
                    <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
                      <button
                        type="button"
                        onClick={() => onManageAccess(rid)}
                        className={cn(
                          'text-[14px] leading-5 px-[11px] py-[7px] rounded-lg border',
                          mediumText,
                          borderPrimary,
                          secondaryText,
                          'hover:bg-background dark:hover:bg-background-dark transition-colors'
                        )}
                      >
                        {t('staff.manage')}
                      </button>
                      {canRemove && (
                        <button
                          type="button"
                          onClick={() => onRemoveRestaurantAccess(rid, name)}
                          aria-label={t('staff.removeAccessButton')}
                          className="text-textRed dark:text-textRed-dark"
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                            <path d="M18 6L6 18" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M6 6L18 18" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          : outOfScopeCount === 0 && (
              <p className={cn(smallTextSize, secondaryText, 'italic')}>{t('staff.noRestaurants')}</p>
            )}

        {outOfScopeCount > 0 && (
          <p className={cn(smallTextSize, secondaryText, 'italic px-1')}>
            {t('staff.moreRestaurantsOutsideAccess', { count: outOfScopeCount })}
          </p>
        )}
      </div>
    </section>
  );
};

export default StaffRestaurantAccessSection;
